import { useState } from 'react';
import { Modal } from 'reactstrap';
import { IconUsers } from '@tabler/icons-react';
import useSdrList, { SdrListItem } from '../../../hooks/useSdrList';
import {
  REASSIGN_SDR_NOT_ASSIGNABLE,
  REASSIGN_SDR_STATUS_NOT_ALLOWED,
  REASSIGN_SDR_VISIT_NOT_FOUND,
  ReassignSdrResult,
} from '../hooks/usePreSaleVisitActions';

const SDR_ROLE = 'SDR_PRESALES';

interface Props {
  currentSdr?: { id: string; name: string };
  onClose: () => void;
  onReassign: (sdrId: string) => Promise<ReassignSdrResult>;
  onReassigned: (sdr: SdrListItem) => void;
}

// Se monta sólo al abrirse, así cada apertura trae la lista de SDR actualizada
// y arranca sin selección ni errores previos.
const ReassignSdrModal = ({
  currentSdr,
  onClose,
  onReassign,
  onReassigned,
}: Props) => {
  const { sdrs, isLoadingSdrs, loadSdrs } = useSdrList(SDR_ROLE);
  const [selectedId, setSelectedId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Reintentar no tiene sentido: la visita cambió de estado o ya no existe.
  const [blocked, setBlocked] = useState(false);

  const options = sdrs.filter(
    (sdr) => sdr.enabled && sdr.id !== currentSdr?.id,
  );
  const selected = options.find((sdr) => sdr.id === selectedId);

  const submit = async () => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const result = await onReassign(selected.id);
      if (result.ok) {
        onReassigned(selected);
        return;
      }
      setError(result.message);
      if (result.code === REASSIGN_SDR_NOT_ASSIGNABLE) {
        setSelectedId('');
        await loadSdrs(SDR_ROLE);
      }
      if (
        result.code === REASSIGN_SDR_STATUS_NOT_ALLOWED ||
        result.code === REASSIGN_SDR_VISIT_NOT_FOUND
      ) {
        setBlocked(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const placeholder = () => {
    if (isLoadingSdrs) return 'Cargando SDRs...';
    return options.length ? 'Selecciona un SDR' : 'No hay otros SDR disponibles';
  };

  return (
    <Modal isOpen toggle={saving ? undefined : onClose} centered className="vfm">
      <div className="vfm-body">
        <div className="vfm-head">
          <span className="vfm-icon">
            <IconUsers size={24} />
          </span>
          <div>
            <h2 className="vfm-title">Reasignar SDR</h2>
            <p className="vfm-sub">
              Solo cambia el SDR: el estado, la fecha, la hora y el proper de la
              visita se mantienen.
            </p>
          </div>
        </div>

        <div className="vfm-field">
          <label>SDR actual</label>
          <div>{currentSdr?.name || 'Sin SDR asignado'}</div>
        </div>

        {!blocked && (
          <div className="vfm-field">
            <label htmlFor="reassign-sdr-select">Nuevo SDR</label>
            <select
              id="reassign-sdr-select"
              className="vfm-input"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={isLoadingSdrs || saving || !options.length}
            >
              <option value="">{placeholder()}</option>
              {options.map((sdr) => (
                <option key={sdr.id} value={sdr.id}>
                  {sdr.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {error && <div className="vfm-error vfm-error--box">{error}</div>}

        <div className="vfm-footer">
          <button
            type="button"
            className="vfm-btn vfm-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            {blocked ? 'Cerrar' : 'Cancelar'}
          </button>
          {!blocked && (
            <button
              type="button"
              className="vfm-btn vfm-btn--primary"
              onClick={submit}
              disabled={!selected || saving}
            >
              {saving ? 'Reasignando…' : 'Reasignar'}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ReassignSdrModal;
